// src/components/latest-results/TestRow.jsx
import UrineColorSelect from "./UrineColorSelect";
import { isUrineColorParam } from "../../Utils/urineColors";

export default function TestRow({
  detail,
  value,
  onChange,
  isCalculated,
  renderUnitRange,
  isCBC,
  rowLoading,
  onFetchOne,
  isCough,
  onOpenCough,
  isCamera,
  onOpenCamera,
  isStethoscope,
  onOpenStethoscope,
}) {
  const canFetch =
    typeof detail?.autoFetchKey === "string" &&
    detail.autoFetchKey.trim() !== "" &&
    detail.autoFetchKey.trim().toUpperCase() !== "N/A";

  const isNumericInput = detail?.inputType?.toLowerCase() === "number";

  // Urine Color is observed by the technician, so it is picked from the swatch
  // dropdown instead of being typed or fetched from the machine.
  const isUrineColor = isUrineColorParam(detail);

  // console.log(value, "--details---", typeof value);

  const cameraValueCount = (() => {
    if (!isCamera) return 0;
    if (Array.isArray(value)) return value.length;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.length;
        }
      } catch (_) {
        // non-JSON string; treat as a single capture
      }
      return 1;
    }
    return value ? 1 : 0;
  })();

  const parseReferenceRange = (referenceRange) => {
    if (typeof referenceRange !== "string") return null;
    const trimmed = referenceRange.trim();
    if (!trimmed || trimmed.toLowerCase() === "n/a" || trimmed === "-") {
      return null;
    }
    if (trimmed.includes(":")) return null;

    const upperOnly = trimmed.match(/^\s*<\s*(-?\d*\.?\d+)\s*$/);
    if (upperOnly) {
      const limit = parseFloat(upperOnly[1]);
      if (!Number.isFinite(limit)) return null;
      return { upper: limit, lower: null, type: "upper" };
    }

    const lowerOnly = trimmed.match(/^\s*>\s*(-?\d*\.?\d+)\s*$/);
    if (lowerOnly) {
      const limit = parseFloat(lowerOnly[1]);
      if (!Number.isFinite(limit)) return null;
      return { lower: limit, upper: null, type: "lower" };
    }

    const match = trimmed.match(
      /(-?\d*\.?\d+)\s*[\u2013-]\s*(-?\d*\.?\d+)/
    );
    if (!match) return null;

    const first = parseFloat(match[1]);
    const second = parseFloat(match[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

    const lower = Math.min(first, second);
    const upper = Math.max(first, second);
    return { lower, upper, type: "range" };
  };

  const range = parseReferenceRange(detail?.referenceRange);
  const numericValue =
    typeof value === "number"
      ? value
      : value !== null && value !== undefined
      ? parseFloat(value)
      : NaN;

  const showVarianceWarning = (() => {
    if (!range || !Number.isFinite(numericValue)) return false;
    if (range.type === "upper") {
      return numericValue > range.upper * 1.3;
    }
    if (range.type === "lower") {
      return numericValue < range.lower * 0.7;
    }
    return (
      numericValue > range.upper * 1.3 || numericValue < range.lower * 0.7
    );
  })();

  return (
    <div className="flex items-center gap-4">
<div className="w-2/5 flex items-center justify-between">
  <span>{detail.name}</span>

  {showVarianceWarning && (
    <span className="text-[16px] text-red-600">
      Inappropriate value detected. Consider retesting.
    </span>
  )}
</div>



      {/* 🔹 camera-enabled rows */}
      {isCamera ? (
        <div className="w-1/6 ml-9 flex items-center gap-2">
          <button
            onClick={() => onOpenCamera?.(detail)}
            className="px-3 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
          >
            {cameraValueCount ? "Retake" : "Capture"}
          </button>
          <span className="text-xs text-gray-600 italic">
            {cameraValueCount
              ? `${cameraValueCount} image${cameraValueCount > 1 ? "s" : ""} captured`
              : "Awaiting capture"}
          </span>
        </div>
      ) : isCough ? (
        <div className="w-1/6 ml-9 flex items-center gap-2">
          <button
            onClick={() => onOpenCough(detail.ParameterID)}
            className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Record Cough
          </button>
          {/* Show current value (Valid / Invalid / Error) if set */}
          {value ? (
            <span className="text-xs text-gray-600 italic">({value})</span>
          ) : null}
        </div>
      ) : isStethoscope ? (
        <div className="w-1/6 ml-9 flex items-center gap-2">
          <button
            onClick={() => onOpenStethoscope?.(detail)}
            className="px-3 py-2 text-sm rounded-md bg-[#845BB3] text-white hover:bg-[#7535bd]"
          >
            {value ? "Re-run Test" : "Open Modal"}
          </button>
          <span className="text-xs text-gray-600 italic">
            {value ? "Result captured" : "Awaiting recording"}
          </span>
        </div>
      ) : isUrineColor ? (
        <div className="w-1/6 ml-9 flex items-center gap-2">
          <UrineColorSelect
            value={value}
            onChange={(colorValue) => onChange(detail.ParameterID, colorValue)}
          />
        </div>
      ) : detail.inputFieldType === "Dropdown" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(detail.ParameterID, e.target.value)}
          className="w-1/6 rounded-md ml-5 px-3 py-2 text-center bg-[#d6d4f8]"
        >
          <option value="">Select</option>
          <option value="negative">Negative</option>
          <option value="positive">Positive</option>
        </select>
      ) : (
        <div className="w-1/6 ml-9 flex items-center gap-2">
          <input
            type="text"
            inputMode={isNumericInput ? "decimal" : undefined}
            value={value || ""}
            onChange={(e) => {
              // ❌ Don't allow editing for calculated values
              if (isCalculated(detail.ParameterID)) return;

              let val = e.target.value;

              if (isNumericInput) {
                // 🚫 Block more than one dot
                const dotCount = (val.match(/\./g) || []).length;
                if (dotCount > 1) return;

                // 🚫 Allow only digits + optional one dot
                if (!/^[0-9]*\.?[0-9]*$/.test(val)) return;
              }

              onChange(detail.ParameterID, val);
            }}
            onBlur={() => {
              if (isCalculated(detail.ParameterID)) return; // don't touch auto values

              if (isNumericInput && value && !isNaN(value)) {
                const num = parseFloat(value);
                if (!isNaN(num)) {
                  onChange(detail.ParameterID, num.toFixed(2)); // ✅ 2-decimal float
                }
              }
            }}
            readOnly={isCalculated(detail.ParameterID)}
            className={`flex-1 min-w-0 rounded-md px-3 py-2 text-center transition-colors ${
              isCalculated(detail.ParameterID)
                ? "bg-yellow-100 cursor-not-allowed"
                : "bg-[#D7D4F8]"
            }`}
            placeholder={
              isCalculated(detail.ParameterID) ? "Auto-calculated" : "Enter Result"
            }
          />
        </div>


      )}

      <div className="w-1/6 text-right text-sm text-[#845BB3]">
        {renderUnitRange(detail)}
      </div>

      <div className="w-1/6 text-right text-sm text-[#845BB3]">
        {isCough ? (
          <span className="italic text-xs text-[#845BB3]">via recording</span>
        ) : isUrineColor ? (
          <span className="italic text-xs text-[#845BB3]">manual entry</span>
        ) : isCBC ? (
          <span className="italic text-xs text-[#845BB3]">via CBC bulk</span>
        ) : canFetch ? (
          <button
            onClick={() => onFetchOne(detail)}
            disabled={rowLoading === detail.ParameterID}
            className="px-3 py-1 bg-[#845BB3] text-white rounded-md text-sm disabled:opacity-60"
          >
            {rowLoading === detail.ParameterID ? "Fetching..." : "Fetch Result"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
